import { useEffect, useRef, useState } from 'react'
import { getMicTimeData, useMicState } from '../micAudio'

const W = 320
const POINTS = 64
const ANNO = '#aec6da'

// One sine wave sampled into an SVG path. `phase` animates it; cycles controls
// how many periods fit across the width (so higher cycles = higher pitch).
function sinePath(yOffset, amp, cycles, phase) {
  let d = ''
  for (let i = 0; i <= POINTS; i++) {
    const u = i / POINTS
    const x = u * W
    const y = yOffset - amp * Math.sin(2 * Math.PI * cycles * u - phase)
    d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `
  }
  return d
}

function micPath(data, yOffset, amp) {
  let d = ''
  const step = Math.floor(data.length / POINTS)
  for (let i = 0; i <= POINTS; i++) {
    const sample = data[Math.min(i * step, data.length - 1)]
    const x = (i / POINTS) * W
    const y = yOffset - ((sample - 128) / 128) * amp * 1.6
    d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `
  }
  return d
}

// Reference wave: cycles=2 puts its first crest at x=W/8 and the next one a
// full period later at x=5W/8 — used below to anchor the amplitude/wavelength
// annotations at fixed spots regardless of the animated phase.
const REF_Y = 210
const REF_AMP = 34
const REF_CYCLES = 2
const CREST_X1 = (W / 8)
const CREST_X2 = CREST_X1 + W / REF_CYCLES

// Amplitude/frequency/wavelength: three stacked waves, matching the desktop
// scene's layout — live mic reactive wave on top, an annotated static
// reference wave in the middle, a faster (higher-pitch) wave at the bottom.
export function Scene02Waveform2D() {
  const mic = useMicState()
  const [topPath, setTopPath] = useState(() => sinePath(70, 30, 1.5, 0))
  const [bottomPath, setBottomPath] = useState(() => sinePath(360, 20, 5, 0))
  const raf = useRef(0)

  useEffect(() => {
    let start = performance.now()
    const loop = (now) => {
      const t = (now - start) / 1000
      const data = mic.active ? getMicTimeData() : null
      setTopPath(data ? micPath(data, 70, 30) : sinePath(70, 30, 1.5, t * 2))
      setBottomPath(sinePath(360, 20, 5, t * 5))
      raf.current = requestAnimationFrame(loop)
    }
    raf.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf.current)
  }, [mic.active])

  const bracketY = REF_Y + REF_AMP + 20

  return (
    <svg viewBox={`0 0 ${W} 460`} style={{ width: '100%', maxWidth: 340, height: 'auto', display: 'block', margin: '0 auto' }}>
      {/* Top: live-reactive wave */}
      <line x1="0" y1="70" x2={W} y2="70" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
      <path d={topPath} fill="none" stroke="#00eeff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />

      {/* Middle: static reference wave with amplitude + wavelength annotations */}
      <line x1="0" y1={REF_Y} x2={W} y2={REF_Y} stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
      <path d={sinePath(REF_Y, REF_AMP, REF_CYCLES, 0)} fill="none" stroke="#8855ff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />

      {/* amplitude: vertical double-arrow from baseline to the first crest */}
      <line x1={CREST_X1} y1={REF_Y} x2={CREST_X1} y2={REF_Y - REF_AMP} stroke={ANNO} strokeWidth="1.5" opacity="0.85" />
      <path d={`M${CREST_X1 - 4},${REF_Y - REF_AMP + 6} L${CREST_X1},${REF_Y - REF_AMP} L${CREST_X1 + 4},${REF_Y - REF_AMP + 6}`} fill="none" stroke={ANNO} strokeWidth="1.5" opacity="0.85" />
      <path d={`M${CREST_X1 - 4},${REF_Y - 6} L${CREST_X1},${REF_Y} L${CREST_X1 + 4},${REF_Y - 6}`} fill="none" stroke={ANNO} strokeWidth="1.5" opacity="0.85" />
      <text x={CREST_X1 - 8} y={REF_Y - REF_AMP / 2} fill={ANNO} fontSize="11" fontFamily="system-ui, sans-serif" textAnchor="end" dominantBaseline="middle">amplitude</text>

      {/* wavelength: bracket between two consecutive crests */}
      <line x1={CREST_X1} y1={REF_Y - REF_AMP} x2={CREST_X1} y2={bracketY} stroke={ANNO} strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
      <line x1={CREST_X2} y1={REF_Y - REF_AMP} x2={CREST_X2} y2={bracketY} stroke={ANNO} strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
      <path d={`M${CREST_X1},${bracketY - 6} L${CREST_X1},${bracketY} L${CREST_X2},${bracketY} L${CREST_X2},${bracketY - 6}`} fill="none" stroke={ANNO} strokeWidth="1.5" opacity="0.85" />
      <text x={(CREST_X1 + CREST_X2) / 2} y={bracketY + 18} fill={ANNO} fontSize="11" fontFamily="system-ui, sans-serif" textAnchor="middle">wavelength</text>

      {/* Bottom: higher frequency = higher pitch */}
      <line x1="0" y1="360" x2={W} y2="360" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
      <path d={bottomPath} fill="none" stroke="#ff4488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <text x={W / 2} y="430" fill="#5d7891" fontSize="11" fontFamily="monospace" textAnchor="middle" letterSpacing="0.05em">higher frequency → higher pitch</text>
    </svg>
  )
}
